/**
 * Dashboard — grouping and selection model (pure, React-free).
 *
 * The overview payload has one row per ProduktInstanz. The page shows those
 * rows folded into groups, chosen by the operator:
 *
 *   - `kunde`   — one group per Kunde: "what do I have to tell this customer?",
 *   - `produkt` — one group per ProduktType: "which deliveries of this product
 *                  are exposed, and at which customers?".
 *
 * The notify gate is a cross-payload join (see `notifyGate` in
 * dashboardModel.ts); this module takes the already computed gates as a map so
 * it stays free of runtime imports and can be tested with plain node.
 *
 * Selection rules — the operator picks WHICH instances get notified:
 *   - `ready` instances with a resolvable Kunde are selectable, and are the
 *     ones a group checkbox selects in bulk,
 *   - `notified` instances are NOT selectable: the advisory went out already,
 *     the status cell links to its Versand entry in the Audit-Log instead,
 *   - everything else (no KEV hit, verdict pending) is not selectable.
 */

import type { OverviewRow } from '../../lib/types';
import type { NotifyGate } from './dashboardModel';

export type GroupMode = 'kunde' | 'produkt';

export type GateMap = ReadonlyMap<number, NotifyGate>;

export interface OverviewGroup {
  /** Unique across both modes (`k:` / `p:` prefix), stable across refreshes. */
  key: string;
  mode: GroupMode;
  /** Kunde name or ProduktType designation. */
  title: string;
  /** Kunde: contact e-mail; Produkt: Modellreihe. */
  subtitle: string | null;
  /** Kunde mode only — CRA reporting duties apply. */
  euKunde: boolean;
  /** Kunde mode only — false when the instances carry no KundeID. */
  hasKunde: boolean;
  rows: OverviewRow[];
  /** Instances with at least one KEV-affected component. */
  affected: number;
  /** Sorted union of the KEV CVEs of all instances. */
  cves: string[];
  /** Distinct counterparts: product types (Kunde mode) or customers (Produkt mode). */
  counterparts: number;
  notified: number;
  ready: number;
  /** KEV hit, verdict not yet BETROFFEN. */
  awaiting: number;
}

function clean(value: string | null | undefined): string {
  return (value ?? '').trim();
}

function groupKey(row: OverviewRow, mode: GroupMode): string {
  if (mode === 'kunde') {
    return row.kunde.id !== null ? `k:${row.kunde.id}` : `k:none:${row.produkt.id}`;
  }
  if (row.produkttyp.id !== null) return `p:${row.produkttyp.id}`;
  const name = clean(row.produkttyp.bezeichnung).toLowerCase();
  return name === '' ? 'p:none' : `p:name:${name}`;
}

/** Fold instance rows into groups. Order: most to notify first, then name. */
export function groupRows(
  rows: readonly OverviewRow[],
  mode: GroupMode,
  gates: GateMap,
): OverviewGroup[] {
  const byKey = new Map<string, OverviewRow[]>();
  for (const row of rows) {
    const key = groupKey(row, mode);
    const bucket = byKey.get(key);
    if (bucket === undefined) byKey.set(key, [row]);
    else bucket.push(row);
  }

  const groups: OverviewGroup[] = [];
  for (const [key, members] of byKey) {
    const first = members[0];
    if (first === undefined) continue;
    const cves = new Set<string>();
    const counterparts = new Set<string>();
    let affected = 0;
    let notified = 0;
    let ready = 0;
    let awaiting = 0;
    for (const row of members) {
      if (row.affected_count > 0) affected += 1;
      for (const cve of row.kev_cves) cves.add(cve);
      counterparts.add(
        mode === 'kunde'
          ? String(row.produkttyp.id ?? clean(row.produkttyp.bezeichnung))
          : String(row.kunde.id ?? `none:${row.produkt.id}`),
      );
      const gate = gates.get(row.produkt.id);
      if (gate?.kind === 'notified') notified += 1;
      else if (gate?.kind === 'ready') ready += 1;
      else if (gate?.kind === 'awaiting-bewertung') awaiting += 1;
    }

    const title =
      mode === 'kunde'
        ? clean(first.kunde.name) || 'Unbekannter Kunde'
        : clean(first.produkttyp.bezeichnung) || 'Unbekannter Produkttyp';
    const subtitle =
      mode === 'kunde' ? clean(first.kunde.email) || null : clean(first.produkttyp.modellreihe) || null;

    groups.push({
      key,
      mode,
      title,
      subtitle,
      euKunde: mode === 'kunde' && first.kunde.ist_eu,
      hasKunde: first.kunde.id !== null,
      rows: members,
      affected,
      cves: [...cves].sort(),
      counterparts: counterparts.size,
      notified,
      ready,
      awaiting,
    });
  }

  return groups.sort(
    (a, b) =>
      b.ready - a.ready ||
      b.affected - a.affected ||
      a.title.localeCompare(b.title, 'de', { sensitivity: 'base' }),
  );
}

/* ------------------------------------------------------------------------- *
 * Selection
 * ------------------------------------------------------------------------- */

/** May this instance be ticked at all? See the module comment. */
export function isSelectable(gate: NotifyGate | undefined): boolean {
  return gate?.kind === 'ready' && gate.kundeResolvable;
}

/**
 * Audit-Log view of the Versand entries of one customer — where an already
 * sent advisory is looked up instead of being sent again.
 */
export function auditLogLink(row: OverviewRow): string {
  const params = new URLSearchParams({ gruppe: 'meldungen' });
  const kunde = clean(row.kunde.name);
  if (kunde !== '') params.set('q', kunde);
  return `/audit-log?${params.toString()}`;
}

/** The instances a group checkbox (de)selects: ready and addressable ones. */
export function bulkIds(group: OverviewGroup, gates: GateMap): number[] {
  return group.rows
    .filter((row) => {
      const gate = gates.get(row.produkt.id);
      return gate?.kind === 'ready' && gate.kundeResolvable;
    })
    .map((row) => row.produkt.id);
}

export type SelectState = 'none' | 'some' | 'all';

/** Tri-state of a group checkbox over its bulk-selectable instances. */
export function groupSelectState(
  group: OverviewGroup,
  gates: GateMap,
  selected: ReadonlySet<number>,
): SelectState {
  const ids = bulkIds(group, gates);
  // Only selectable ids count: a stale id (instance notified meanwhile) must
  // not leave the group checkbox half-ticked.
  const picked = ids.filter((id) => selected.has(id)).length;
  if (picked === 0) return 'none';
  return picked === ids.length ? 'all' : 'some';
}

/**
 * The selection that is still valid against the live payload: a refresh may
 * drop an instance or move it to a state that is no longer selectable.
 * Returned in payload order, which is also the order the server drafts in.
 */
export function effectiveSelection(
  rows: readonly OverviewRow[],
  gates: GateMap,
  selected: ReadonlySet<number>,
): OverviewRow[] {
  return rows.filter(
    (row) => selected.has(row.produkt.id) && isSelectable(gates.get(row.produkt.id)),
  );
}

/** Distinct customers of a selection — the number of advisories it drafts. */
export function customerCount(rows: readonly OverviewRow[]): number {
  return new Set(rows.map((row) => (row.kunde.id !== null ? `k${row.kunde.id}` : `p${row.produkt.id}`)))
    .size;
}
